import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, inject, WritableSignal, signal, Signal, computed } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Observable, throwError, tap, catchError, finalize } from "rxjs";
import { environment } from "../../../../../environments/environment";
import { AuthService } from "./auth.service";
import { ProjectSettingsService } from "./project-settings.service";


export interface CrmTicketRequest {
  fullReportId: string;
  telegramUsername: string;
  companyName: string;
  companyId: string;
  providerReports: string[];
  userId: string;
  adminPanelReportLink: string;
  source: 'GENERAL' | 'RECOVERY_PARTNER' | 'SALES' | 'WALLET_REPORT' | 'ITR' | 'OTR' | 'API';
  domain: 'SC' | 'SMC' | 'AML';
  subject?: string;
}

export interface SupportTicketContext {
  fullReportId?: string;
  providerReports?: string[];
  adminPanelReportLink?: string;
}

export interface SupportActionParams {
  source?: 'GENERAL' | 'RECOVERY_PARTNER' | 'SALES' | 'WALLET_REPORT' | 'ITR' | 'OTR' | 'API';
  subject?: string;
  ticket?: SupportTicketContext;
}

@Injectable({
  providedIn: 'root'
})
export class TelegramIdentifyService {
  private readonly _httpClient: HttpClient = inject(HttpClient);
  public readonly authService: AuthService = inject(AuthService);
  public readonly projectSettingsService: ProjectSettingsService = inject(ProjectSettingsService);

  private readonly supportUrl: Signal<string> = computed(() => {
    const username = this.projectSettingsService.settings()?.supportBotUsername ?? '';
    const normalized = username.startsWith('@') ? username.slice(1) : username;
    return `https://telegram.me/${normalized}`;
  });
  public readonly crmTicketLoading: WritableSignal<boolean> = signal(false);
  public readonly crmTicketSuccess: WritableSignal<boolean> = signal(false);
  public readonly crmTicketError: WritableSignal<string | null> = signal(null);

  handleSupportAction(params?: SupportActionParams): string | void {
    const account = this.authService.accountDto();
    const user = account?.user;

    const hasTelegram = !!(user?.telegramUsername || user?.telegramChatId);

    if (hasTelegram) {
      console.log('hasTelegram', params);
      const ticketPayload = this.buildTicketPayload(params);
      console.log('ticketPayload', ticketPayload);
      if (ticketPayload) {
        console.log('ticketPayload', ticketPayload);
        this.openSupportLink();
        // TODO: Return after fixed crm
        // this.createCrmTicket(ticketPayload).subscribe({
        //   next: () => {
        //     this.openSupportLink();
        //   },
        //   error: (error) => {
        //     console.log('error', error);
        //     console.error('CRM ticket creation failed', error);
        //     this.openSupportLink();
        //   }
        // });
      }
    }
  }

  private createCrmTicket(payload: CrmTicketRequest): Observable<unknown> {
    const validationError = this.validateTicketPayload(payload);
    if (validationError) {
      this.crmTicketError.set(validationError);
      return throwError(() => new Error(validationError));
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${environment.crmApiKey}`,
      'Content-Type': 'application/json'
    });

    this.crmTicketLoading.set(true);
    this.crmTicketError.set(null);
    this.crmTicketSuccess.set(false);

    return this._httpClient.post(`${environment.crmApiUrl}/api/tickets/`, payload, { headers }).pipe(
      tap(() => this.crmTicketSuccess.set(true)),
      catchError((error) => {
        this.crmTicketError.set(error?.error?.message || 'Failed to create CRM ticket');
        return throwError(() => error);
      }),
      finalize(() => this.crmTicketLoading.set(false))
    );
  }

  private buildTicketPayload(params?: SupportActionParams): CrmTicketRequest | null {
    const account = this.authService.accountDto();
    const user = account?.user;
    const company = account?.companyDto;
    const ticket = params?.ticket;



    return {
      fullReportId: ticket?.fullReportId || '0',
      telegramUsername: user?.telegramUsername || '',
      companyName: company?.name || '',
      companyId: company?.id || '',
      providerReports: ticket?.providerReports || [],
      userId: user?.uuid || '',
      adminPanelReportLink: ticket?.adminPanelReportLink || '',
      source: params?.source || 'GENERAL',
      subject: params?.subject || 'General Support',
      domain: 'AML'
    };
  }

  private openSupportLink(): void {
    const url = this.supportUrl();

    if (!url || url === 'https://telegram.me/') {
      console.warn('[TelegramIdentifyService] supportBotUsername is empty, cannot open support link');
      return;
    }

    const telegramWebApp = (window as any)?.Telegram?.WebApp;

    if (telegramWebApp?.openTelegramLink) {
      telegramWebApp.openTelegramLink(url);
      return;
    }

    if (telegramWebApp?.openLink) {
      telegramWebApp.openLink(url, { try_instant_view: false });
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private validateTicketPayload(payload: CrmTicketRequest): string | null {
    if (payload.subject === 'WALLET_REPORT' || payload.subject === 'ITR' || payload.subject === 'OTR') {
      if (!payload.fullReportId || Number.isNaN(Number(payload.fullReportId))) {
        return 'fullReportId is required';
      }
      if (!payload.adminPanelReportLink) {
        return 'adminPanelReportLink is required';
      }
    }
    if (this.authService.accountDto()?.accountType === 'CORPORATE') {
      if (!payload.companyId) {
        return 'companyId is required';
      }
      if (!payload.companyName) {
        return 'companyName is required';
      }
    }

    if (!payload.telegramUsername) {
      return 'telegramUsername is required';
    }

    if (!payload.userId) {
      return 'userId is required';
    }

    return null;
  }
}
